import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { setShowTransitionDialog } from "../../../State/actions";
import TickerTimeout from "../../../utils/TickerTimeout";
import SpineAtlasUploader from "../../../utils/SpineAtlasUploader";
import { Settings } from "../../../settings";
import { PixiSettings } from "../../../pixiSettings";
import { soundManager } from "../../../SoundManager";
import { SOUND_NAME } from "../../../sounds";

const SPINE_NAME = "transition";

// The export ships a single unnamed clip. Listed as candidates so a re-export
// that gives it a real name keeps working.
const ANIM = ["animation", "transition", "in"];

// Safety net. The close is driven by the animation's `complete` event; if that
// never arrives the transition would sit over the game for ever.
const COMPLETE_MAX_WAIT_PADDING_MS = 500;

// Hand-added to transition.json at t=1.0 (the export ships no events). The clip
// is 2.0s and the `dark` smoke (dark_smoke_dust10) is fully opaque from 0.6667
// to 1.3333, so 1.0 is its centre — the pit stop: swap the scene, flip state.
// The smoke alone does not reach the screen's edges; the smoke's edges below
// cover them. A re-export deletes the event — re-add it.
//
// Same idea Zeus used — `transition_midpoint` in game_view.json, consumed by
// GameViewController.playTransition to swap skin and idle under cloud cover.
const MIDPOINT_EVENT = "transition_midpoint";

// ── The smoke's edges ──
//
// Since the 2026-10-07 fireball export the rig has no full-screen plate — its
// `dark` smoke is a soft blob, opaque only in its middle — so at the midpoint
// the screen's edges showed the scene swapping (measured: 23-57% of the screen
// still let the scene through at every frame, desktop to portrait).
// A soft vignette goes UNDER the spine: clear in the middle, where the smoke
// already covers, black towards the edges. Its strength is the dark smoke's
// own opacity, and only around the midpoint, so it reads as the smoke
// thickening, not as a black screen. Tuned offline against the posed rig: at
// the midpoint at most 0.8% shows through, on every layout.
const COVER_SLOT = "dark_smoke_dust10";
// Portrait only (client, 2026-10-12): the fireballs run top -> bottom instead
// of left -> right — the whole wipe turned a quarter, clockwise (PIXI's y is
// down, so +90° takes +x onto +y). Its dark cover is wider than tall, so
// turned it runs taller than wide: a better fit for a portrait screen. 0 = as
// authored.
const PORTRAIT_ROTATION = Math.PI / 2;
// Clear inside EDGE_CLEAR, fully dark beyond EDGE_DARK — shares of the
// screen's half size (an ellipse on the screen).
const EDGE_CLEAR = 0.25;
const EDGE_DARK = 0.35;
// Clip times, s: ramps in, full across the midpoint, gone again.
const EDGE_IN = [0.667, 0.85];
const EDGE_OUT = [1.15, 1.333];

const smoothstep = (e0, e1, x) => {
  if (x <= e0) return 0;
  if (x >= e1) return 1;
  const t = (x - e0) / (e1 - e0);
  return t * t * (3 - 2 * t);
};

/** A radial black vignette, drawn once: clear centre, black edges. */
const createEdgeTexture = () => {
  const size = 256;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d");
  const r = size / 2;
  const gradient = ctx.createRadialGradient(r, r, 0, r, r, r);
  gradient.addColorStop(0, "rgba(0,0,0,0)");
  gradient.addColorStop(EDGE_CLEAR, "rgba(0,0,0,0)");
  // A smoothstep, in steps.
  for (let i = 1; i < 8; i++) {
    const t = i / 8;
    gradient.addColorStop(
      EDGE_CLEAR + (EDGE_DARK - EDGE_CLEAR) * t,
      `rgba(0,0,0,${(t * t * (3 - 2 * t)).toFixed(3)})`,
    );
  }
  gradient.addColorStop(EDGE_DARK, "rgba(0,0,0,1)");
  gradient.addColorStop(1, "rgba(0,0,0,1)");
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  return PIXI.Texture.from(canvas);
};

/**
 * TransitionDialogController — the base <-> bonus scene wipe.
 *
 * Not a popup. It takes no input, has no tap-to-continue and no skip
 * protection: it plays once and closes itself. What it shares with the popups
 * is the shell — the component never unmounts and visibility is alpha.
 *
 * What it does NOT share is their dimming overlay. Every other popup fades a
 * black Graphics in behind itself; this one is a full-screen wipe that covers
 * and reveals on its own, so a second layer only ever delayed the reveal. See
 * finishAnimation.
 *
 * Replaces FreeSpinSceneTransition, whose `free_spin_transition` spine was
 * Zeus's and is no longer registered.
 */
class TransitionDialogController {
  constructor(container, dimensionsContext, dispatch, symbolHeight) {
    this.container = container;
    this.dimensionsContext = dimensionsContext;
    this.dispatch = dispatch;
    this.symbolHeight = symbolHeight;

    this.dimensions = dimensionsContext?.dimensions;

    this.spine = null;
    this.isPlaying = false;
    this.initialized = false;
    this._showRAF = null;
    this._safetyTimeout = null;
    this._listener = null;
    // The smoke's edges (see COVER_SLOT) and its per-frame strength.
    this.edges = null;
    this._edgeTick = null;

    // Fired when the screen is fully covered. Set by whoever owns the scene
    // swap; the transition itself does not care what happens here.
    this._midpointHandler = null;

    // Fired when the wipe is FULLY GONE — the clip's own last frame. Where
    // anything the player may touch or see is switched back on; see
    // setCompleteHandler.
    this._completeHandler = null;

    // Bumped on every close, so a sequence that outlives its own run cannot
    // finish a later one.
    this._run = 0;

    this.initializeSpine();
  }

  /**
   * Register the work to run while the screen is covered.
   *
   * @param {Function|null} handler Called once per play, at MIDPOINT_EVENT.
   */
  setMidpointHandler(handler) {
    this._midpointHandler = typeof handler === "function" ? handler : null;
  }

  /**
   * Register the work to run once the wipe is off the screen.
   *
   * The pair to setMidpointHandler, and the split between them is the whole
   * rule for this transition:
   *
   *   MIDPOINT  the screen is covered — swap the scene, flip the state nobody
   *             can see. Instant work only.
   *   COMPLETE  the screen is clear — start what the player WATCHES, and
   *             re-enable what they can touch.
   *
   * Nothing is enabled at the midpoint any more. There is no point in a live
   * button or a released lock behind a cover that still hides the board, and
   * while it lasted the only thing holding a mid-wipe tap out was the dimming
   * overlay that used to sit under the spine.
   *
   * Called on the clip's `complete`, and on the safety timeout that stands in
   * for it, so it cannot be skipped.
   *
   * @param {Function|null} handler Called once per play, after the last frame.
   */
  setCompleteHandler(handler) {
    this._completeHandler = typeof handler === "function" ? handler : null;
  }

  // ── Setup ────────────────────────────────────────────────────────────────

  initializeSpine() {
    try {
      const assets = PIXI.Assets.cache;
      if (
        !assets.has(`${SPINE_NAME}-skeleton`) ||
        !assets.has(`${SPINE_NAME}-atlas`)
      ) {
        console.warn("[TransitionDialog] Spine not loaded:", SPINE_NAME);
        return;
      }

      this.spine = Spine.from({
        skeleton: `${SPINE_NAME}-skeleton`,
        atlas: `${SPINE_NAME}-atlas`,
      });
      if (!this.spine?.skeleton || !this.spine?.state) return;

      // The smoke's edges under the spine (see COVER_SLOT), hidden until a wipe.
      this.edges = new PIXI.Sprite(createEdgeTexture());
      this.edges.name = "transition_smoke_edges";
      this.edges.anchor.set(0.5);
      this.edges.alpha = 0;
      this.edges.eventMode = "none";
      this.container.addChild(this.edges);
      this.container.addChild(this.spine);
      // Hidden by ALPHA, never by unmounting.
      // ── Hidden at alpha 0, not 0.001 ──
      //
      // 0.001 still RENDERS: PIXI skips a subtree only at `worldAlpha <= 0`. The
      // near-zero was there to keep PIXI's texture GC from unloading this spine's
      // atlas after 60 idle seconds — and that sweep is now off (see Stage.js), so
      // the trick has nothing left to buy. At 0 the whole subtree is skipped, and
      // any fade that raises alpha still brings it back exactly as before.
      this.spine.alpha = 0;
      if (this.spine) this.spine.autoUpdate = false;
      this.spine.zIndex = 20;

      this.applyScaleAndCenter();
      this.initialized = true;
      this.warmUpSpine();
    } catch (error) {
      console.warn("[TransitionDialog] Init failed:", error);
    }
  }

  warmUpSpine() {
    if (!this.spine?.state) return;
    try {
      const name = this._resolveAnim();
      if (name) {
        if (this.spine) this.spine.autoUpdate = true;
        this.spine.state.setAnimation(0, name, false);
        this.spine.state.update(0.016);
        // Spine 4.2 REQUIRES an argument here.
        if (this.spine.skeleton.physics !== undefined) {
          this.spine.skeleton.updateWorldTransform(2);
        } else {
          this.spine.skeleton.updateWorldTransform(0);
        }
        this.spine.state.clearTracks();
      }
      this.spine.alpha = 0;
      if (this.spine) this.spine.autoUpdate = false;
      SpineAtlasUploader.upload(SPINE_NAME);
    } catch {
      // Optimisation only.
    }
  }

  _resolveAnim() {
    const animations = this.spine?.skeleton?.data?.animations;
    if (!animations) return null;
    for (const name of ANIM) {
      if (animations.find((a) => a.name === name)) return name;
    }
    // Single-clip export under an unexpected name — take whatever it has.
    return animations[0]?.name ?? null;
  }

  getAnimationDuration(name) {
    const anim = this.spine?.skeleton?.data?.animations?.find(
      (a) => a.name === name,
    );
    return anim?.duration ? Math.round(anim.duration * 1000) : 1000;
  }

  // ── Sizing ───────────────────────────────────────────────────────────────

  /** The same rule every popup uses. */
  applyScaleAndCenter() {
    if (!this.spine || !this.dimensions) return;

    const nativeSize = Settings.SYMBOL_HEIGHT;
    const targetSize = this.symbolHeight || nativeSize;
    const scale = Math.max(0.1, Math.min(targetSize / nativeSize, 2));

    this.spine.scale.set(scale);
    // The parent container sits at the screen centre.
    this.spine.x = 0;
    this.spine.y = 0;
    // Turned in portrait (PORTRAIT_ROTATION); about its own centre, the origin.
    const portrait =
      this.dimensionsContext?.orientation === PixiSettings.ORIENTATION.PORTRAIT;
    this.spine.rotation = portrait ? PORTRAIT_ROTATION : 0;
    // The edges cover the whole screen (they are not scaled with the spine).
    if (this.edges) {
      this.edges.position.set(0, 0);
      this.edges.width = this.dimensions.width;
      this.edges.height = this.dimensions.height;
    }
  }

  /** The edges' strength: the dark smoke's opacity, around the midpoint only. */
  _startEdges() {
    this._stopEdges();
    const slot = this.spine?.skeleton?.findSlot(COVER_SLOT);
    if (!this.edges || !slot) return;
    this._edgeTick = () => {
      const t = this.spine?.state?.tracks?.[0]?.trackTime ?? 0;
      const around =
        smoothstep(EDGE_IN[0], EDGE_IN[1], t) * (1 - smoothstep(EDGE_OUT[0], EDGE_OUT[1], t));
      this.edges.alpha = (this.spine?.alpha ?? 0) * slot.color.a * around;
    };
    PIXI.Ticker.shared.add(this._edgeTick);
  }

  _stopEdges() {
    if (this._edgeTick) PIXI.Ticker.shared.remove(this._edgeTick);
    this._edgeTick = null;
    if (this.edges) this.edges.alpha = 0;
  }

  updateDimensions(dimensionsContext, symbolHeight) {
    this.dimensionsContext = dimensionsContext;
    this.dimensions = dimensionsContext?.dimensions;
    this.symbolHeight = symbolHeight;
    this.applyScaleAndCenter();
  }

  // ── Sequence ─────────────────────────────────────────────────────────────

  /** Play once, then close. Nothing waits for the player. */
  startSequence() {
    if (this.isPlaying || !this.spine?.state) return;

    const name = this._resolveAnim();
    if (!name) {
      console.warn("[TransitionDialog] No animation to play");
      return;
    }

    this.isPlaying = true;
    const run = ++this._run;
    this._clearTimeouts();

    this.applyScaleAndCenter();

    this.spine.state.clearTracks();
    this.spine.state.clearListeners();
    this.spine.skeleton.setToSetupPose();

    // ── The wipe, as a sound ──
    //
    // Here rather than in the rAF below, so the sound and the clip start on
    // the same beat. The one frame it leads the visible wipe by is the frame
    // the warm-up pose is being cleared in, and it is not perceptible — a
    // sound that starts WITH a fast wipe reads as late, never as early.
    //
    // startSequence is the only entry point and returns early on isPlaying,
    // so this is once per wipe in both directions.
    soundManager.play(SOUND_NAME.transition);

    // Played while STILL HIDDEN, shown on the NEXT frame — the warm-up pose is
    // gone by then and the clip starts from its own first frame.
    if (this.spine) this.spine.autoUpdate = true;
    this.spine.state.setAnimation(0, name, false);
    this._startEdges();
    if (this._showRAF) cancelAnimationFrame(this._showRAF);
    this._showRAF = requestAnimationFrame(() => {
      this._showRAF = null;
      if (this.isPlaying && this.spine) this.spine.alpha = 1;
    });

    this._listener = {
      event: (entry, event) => {
        if (event.data.name !== MIDPOINT_EVENT || this._run !== run) return;
        // Guarded: a handler that throws must not strand the wipe on screen.
        try {
          this._midpointHandler?.();
        } catch (error) {
          console.warn("[TransitionDialog] Midpoint handler failed:", error);
        }
      },
      complete: (entry) => {
        if (entry.animation.name === name && this._run === run) {
          this.finishAnimation();
        }
      },
    };
    this.spine.state.addListener(this._listener);

    // If `complete` never arrives the wipe would cover the game for ever.
    this._safetyTimeout = new TickerTimeout(() => {
      if (this._run === run && this.isPlaying) this.finishAnimation();
    }, this.getAnimationDuration(name) + COMPLETE_MAX_WAIT_PADDING_MS);
  }

  finishAnimation() {
    if (!this.spine || !this.initialized) return;

    this.isPlaying = false;
    this._run++;
    this.spine.alpha = 0;
    if (this.spine) this.spine.autoUpdate = false;
    this._stopEdges();

    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }
    this._clearTimeouts();
    if (this._listener) {
      this.spine.state?.removeListener(this._listener);
      this._listener = null;
    }

    // ── The spine's last frame IS the end of the wipe ──
    //
    // There used to be a black Graphics behind it at alpha 0.85, faded in on
    // open and out over 350ms from HERE — so the flag dropped on the clip's
    // `complete` but the board spent another third of a second coming out from
    // behind an overlay the spine never needed. That is what made the bonus
    // room's entry pips look like they started early.
    //
    // The rig covers the screen on its own and reveals on its own:
    //
    //   cover2 alpha  00 -> ff @0.333 -> ff @0.633 -> 00 @1.033
    //   cover1 alpha  00 -> be @0.333 -> be @0.633 -> 00 @1.033
    //   both are rectangle_full (20x20) at scale 136 = 2720x2720 units
    //
    // Fully opaque across `transition_midpoint` at 0.5, which is the only
    // instant that has to be covered — the scene swap happens there. Its own
    // fade to nothing by 1.0333 is the reveal. A second, slower reveal behind
    // it was doing nothing but delaying the room.
    //
    // So the wipe is the spine and only the spine: the midpoint event swaps the
    // scene, `complete` ends it, and this dispatch is the end.
    this.dispatch?.(setShowTransitionDialog(false));

    // Guarded for the same reason the midpoint call is: a handler that throws
    // must not leave the wipe half-closed.
    try {
      this._completeHandler?.();
    } catch (error) {
      console.warn("[TransitionDialog] Complete handler failed:", error);
    }
  }

  _clearTimeouts() {
    if (this._safetyTimeout) {
      this._safetyTimeout.clear();
      this._safetyTimeout = null;
    }
  }

  cleanup() {
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }
    this._clearTimeouts();
    this._stopEdges();
    if (this.edges) {
      this.edges.destroy({ texture: true, baseTexture: true });
      this.edges = null;
    }

    if (this.spine) {
      this.spine.state?.clearListeners();
      this.spine.state?.clearTracks();
      if (this.container?.children.includes(this.spine)) {
        this.container.removeChild(this.spine);
      }
      this.spine.destroy();
      this.spine = null;
    }
    this._listener = null;
    this.initialized = false;
  }
}

export default TransitionDialogController;
